import {
  Component,
  Input,
  Output,
  EventEmitter,
  ChangeDetectorRef,
  ElementRef,
  NgZone,
  ViewChild,
  AfterViewInit,
  Inject
} from '@angular/core';
import {Subject} from 'rxjs';
import {take} from 'rxjs/operators';
import { DOCUMENT } from '@angular/common';
@Component({
  selector: 'axis-chip',
  templateUrl: './chip.component.html',
  host: {
    'role': 'option',
    '[class.disabled]': 'disabled',
    '[attr.aria-disabled]': 'disabled ? disabled.toString() : null',
    '[attr.aria-selected]': 'selected ? true : false',
    '(focus)': 'focus()',
    '(blur)': '_blur()',
  }
})
export class AxisChipComponent implements AfterViewInit {
  _hasFocus = false;
  /** Emits when the chip is focused. */
  readonly _onFocus = new Subject<any>();
  /** Emits when the chip is blured. */
  readonly _onBlur = new Subject<any>();
  ariaSelected: string | null;
  chipListSelectable: boolean;
  trailingIcon: any;
  protected _selected = false;
  @Input() color: any;
  @Input() disableRipple: boolean;
  @Input() disabled: boolean;
  @Input() removable = false;
  @Input() clickable = true;
  @Input() value: any;
  @Input() avatar: any;
  @Input() heading: any;
  @Input() icon: any;
  @Input() iconColor: any;
  @Input() selectedIconColor: any;
  @Input() iconFont: any;
  @Input() selectedIcon: any;
  @Input() selectedIconFont: any;
  @Input() removeIcon = 'cancel';
  @Output() selectionChange: EventEmitter<any> = new EventEmitter();
  @Output() removed: EventEmitter<any> = new EventEmitter();
  @ViewChild('content') content: any;
  /** Whether the chip is selected. */
  @Input()
  get selected(): boolean { return this._selected; }
  set selected(value: boolean) {
      this._selected = value;
  }
  constructor(
    @Inject(DOCUMENT) private _document: any,
    private changeDetectorRef: ChangeDetectorRef,
    public _elementRef: ElementRef<HTMLElement>,
    private _ngZone: NgZone) { }

  ngAfterViewInit(): void {
    const innerHTML = this.content.nativeElement.innerHTML;
    if (!innerHTML) {
      this.content.nativeElement.innerHTML = this.heading;
    } else if (!this.heading) {
      this.content.nativeElement.innerHTML = innerHTML || this.value;
    } else {
      this.content.nativeElement.innerHTML = this.heading + ' ' + innerHTML;
    }

  }

  /** Allows for programmatic focusing of the chip. */
  focus(): void {
    if (this.clickable && !this.disabled) {
      if (!this._hasFocus) {
        this._elementRef.nativeElement.focus();
        this._onFocus.next({chip: this});
      }
      this._hasFocus = true;
    }
  }

  private _dispatchSelectionChange() {
    this.selectionChange.emit({
      value: this.value,
      heading: this.heading,
      selected: this._selected,
      removable: this.removable
    });
  }

  /** Handles click events on the chip. */
  _handleClick(event: Event) {
    if (this.disabled || !this.clickable) {
      event.preventDefault();
    } else {
      if (this.selected) {
        this.deSelect();
      } else {
        this.select();
      }
      event.stopPropagation();
    }
  }

   /** Handle custom key presses. */
   _handleKeydown(event: KeyboardEvent): void {
    const keyCode = event.keyCode;

    switch (keyCode) {
      case 46:
      case 8:
        if (!this.disabled && this.clickable) {
          this.deSelect();
        }
        if (!this.disabled && this.removable) {
          this._handleRemoveClick(event);
        }
        // Always prevent so page navigation does not occur
        event.preventDefault();
        break;
      case 32:
      case 13:
        if (!this.disabled && this.clickable) {
          this._handleClick(event);
        }
        // Always prevent space from scrolling the page since the list has focus
        event.preventDefault();
        break;
    }
  }

  _handleRemoveKeydown(event: KeyboardEvent): void {
    const keyCode = event.keyCode;

    switch (keyCode) {
      case 46:
      case 8:
      case 32:
      case 13:
        if (!this.disabled) {
          this._handleRemoveClick(event);
        }
        // Always prevent space from scrolling the page since the list has focus
        event.preventDefault();
        break;
    }
  }

  private _markForCheck() {
    // @breaking-change 9.0.0 Remove this method once the _changeDetectorRef is a required param.
    if (this.changeDetectorRef) {
      this.changeDetectorRef.markForCheck();
    }
  }

  _blur(): void {
    // When animations are enabled, Angular may end up removing the chip from the DOM a little
    // earlier than usual, causing it to be blurred and throwing off the logic in the chip list
    // that moves focus not the next item. To work around the issue, we defer marking the chip
    // as not focused until the next time the zone stabilizes.
    this._ngZone.onStable
      .asObservable()
      .pipe(take(1))
      .subscribe(() => {
        this._ngZone.run(() => {
          if (this.clickable && !this.disabled) {
          this._hasFocus = false;
            this._onBlur.next({ chip: this });
          }
        });
      });
  }

   /** Select this chip and emit selected event */
   selectViaInteraction(): void {
    this.select();
  }

  select(isAdd = false) {
    if (!this._selected || isAdd) {
      this._selected = true;
      this._dispatchSelectionChange();
      this._markForCheck();
    }
  }
  deSelect() {
    this._selected = false;
    this._dispatchSelectionChange();
    this._markForCheck();
  }

  _handleRemoveClick(event) {
    event.stopPropagation();
    if (this.selected) {
      this.deSelect();
      setTimeout(() => {
        this.removeFromDOM();
      }, 0);
    } else {
      this.removeFromDOM();
    }
    this._dispatchRemove();
  }

  private _dispatchRemove() {
    this.removed.emit({
      value: this.value,
      heading: this.heading
    });
  }

  private removeFromDOM() {
    if (this.removable && this._elementRef && this._elementRef.nativeElement && this._elementRef.nativeElement.parentNode) {
      let nextElement: any = this._elementRef.nativeElement.nextElementSibling;
      if (!nextElement) {
        nextElement = this._elementRef.nativeElement.previousElementSibling;
      }
      this._elementRef.nativeElement.parentNode.removeChild(this._elementRef.nativeElement);
      const tooltipContainer = this._document.querySelector('body > tooltipcontainer');
      if (tooltipContainer) {
        this._document.body.removeChild(tooltipContainer);
      }
      if (nextElement && nextElement.children[0]) {
        nextElement.children[0].focus();
      }
    }
  }
}

@Component({
  selector: 'axis-chip-avatar',
  template: '<ng-content></ng-content>'
})
export class AxisChipAvatarComponent {
}
